import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { WidgetTranslationDto } from '@/Domain/api/WIdget/dto/WidgetTranslation.dto';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { isFilled } from '@/UI/Website/TextContent/translationStatus';

type Props = {
  form: UseFormReturn<WidgetTranslationDto>;
  language: string;
  country: string;
  // The default (primary) values each field falls back to, so the preview renders
  // what a visitor actually sees for an untranslated field rather than a blank.
  fallbacks: Record<string, unknown>;
};

/** Push one translation field to the preview iframe, mapping dashboard field
 * names to the widget ConfigurationDto names where they differ (same remaps as
 * the primary-language editor in useUpdateWidgetSettings). Empty fields fall back
 * to the default, mirroring the backend resolver. */
const pushPreviewField = (
  setConfiguration: (key: string, value: unknown) => void,
  key: string,
  value: unknown,
  fallbacks: Record<string, unknown>,
): void => {
  if (key === 'language' || key === 'country') return;
  if (key === 'conversation_starters') {
    const primary = (fallbacks.conversation_starters as string[]) ?? [];
    const target = (value as string[]) ?? [];
    setConfiguration(
      'conversation_starters',
      primary.map((p, index) => (isFilled(target[index]) ? target[index] : p)),
    );
    return;
  }
  const effective = isFilled(value) ? value : (fallbacks[key] ?? '');
  if (key === 'welcome_message') {
    setConfiguration('welcome_messages', effective ? [effective] : []);
    return;
  }
  if (key === 'background_disclaimer_text') {
    setConfiguration('background_text', effective ?? '');
    return;
  }
  setConfiguration(key, effective);
};

export const useUpdateWidgetTranslation = ({ form, language, country, fallbacks }: Props) => {
  const { t } = useTranslation('website');
  const queryClient = useQueryClient();
  const { setConfiguration } = usePreview();

  // Push every field change to the preview iframe so it renders this language.
  const formValues = useWatch({ control: form.control });
  const fallbacksRef = useRef(fallbacks);
  fallbacksRef.current = fallbacks;
  const prevValuesRef = useRef<Record<string, unknown>>({});
  useEffect(() => {
    const prev = prevValuesRef.current;
    Object.entries(formValues)
      .filter(([key, value]) => !isEqual(prev[key], value))
      .forEach(([key, value]) =>
        pushPreviewField(setConfiguration, key, value, fallbacksRef.current),
      );
    prevValuesRef.current = { ...formValues };
  }, [formValues, setConfiguration]);

  const mutation = useMutation<
    WidgetTranslationDto,
    unknown,
    WidgetTranslationDto,
    { previous?: WidgetTranslationDto[] }
  >({
    mutationKey: [QueryKey.widgetTranslations.upsert],
    onMutate: (data) => {
      // Snapshot the list so a failed PUT can be rolled back — otherwise the
      // optimistic write below leaves the rail showing edits as saved until reload.
      const previous = queryClient.getQueryData<WidgetTranslationDto[]>([
        QueryKey.widgetTranslations.list,
      ]);
      queryClient.setQueryData(
        [QueryKey.widgetTranslations.list],
        (prev: WidgetTranslationDto[] = []) => {
          const others = prev.filter(
            (row) => !(row.language === data.language && row.country === data.country),
          );
          return [...others, data];
        },
      );
      return { previous };
    },
    onError: (error: any, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData([QueryKey.widgetTranslations.list], context.previous);
      }
      toast.error(
        error.response?.data?.errors?.root?.[0]?.message || t('appearance.form.error.common'),
      );
    },
  });

  // partial=false: the PUT is a full upsert, so always send the whole row
  // (a partial diff would null out the untouched fields). The dirty guard stops
  // the autosubmit from firing on mount — otherwise merely selecting/adding a
  // language would PUT an empty row before the user has edited anything.
  // flushOnUnmount: the editor remounts on every rail switch and unmounts on
  // sheet close — a cancelled debounce there silently drops the last edit.
  useAutoSubmit<WidgetTranslationDto>(
    form,
    () => {
      if (!form.formState.isDirty) return;
      // A save (debounced or flushed on unmount) must not resurrect a row the user
      // deleted — its optimistic removal from the list cache marks it deleted.
      const rows = queryClient.getQueryData<WidgetTranslationDto[]>([
        QueryKey.widgetTranslations.list,
      ]);
      if (rows && !rows.some((row) => row.language === language && row.country === country)) {
        return;
      }
      mutation.mutate({ ...form.getValues(), language, country });
    },
    false,
    true,
  );
};
